import React, { useMemo, useState } from "react";
import { CardCarousel, sanitize } from "@americanexpress/consumer-travel-base";
import { ArticleSet } from "../../types/PersonalizedMarketingArticles";
import styles from "../MarketingArticleStyles.css";
import { ArticleOrAd } from "../ArticleOrAd/ArticleOrAd";
import { useReactlytics } from "@americanexpress/reactlytics";
import { MODULE_NAME } from "../../constants/moduleConstants";
import { FilterButtons } from "./ArticleFilterButtons";
import { getActiveArticles, getTags } from "../../utils/articles";
import { clsx } from "clsx";
import { useArticles } from "../../hooks/useArticles";
import { mediaQueries } from "@americanexpress/dls-react";

type ArticleLayoutProps = {
  heading?: string;
  articles?: ArticleSet;
  analyticsElementPrefix?: string;
  articlesOverrideKey?: string;
  clientId?: string;
  placementId?: string;
};

export const ArticleLayout = ({
  heading: headingProp,
  articles: articlesProp,
  analyticsElementPrefix: analyticsElementPrefixProp,
  articlesOverrideKey,
  clientId,
  placementId,
}: ArticleLayoutProps) => {
  const {
    data: oneDataArticlesData,
  } = useArticles({
    clientId,
    placementId,
    articlesOverrideKey,
  })

  const {
    title: oneDataTitle,
    articles: oneDataArticles,
    analyticsElementPrefix: oneDataAnalyticsElementPrefix,
  } = oneDataArticlesData || {};

  const heading = oneDataTitle || headingProp;
  const articles = oneDataArticles || articlesProp;
  const analyticsElementPrefix = oneDataAnalyticsElementPrefix || analyticsElementPrefixProp;

  const { track } = useReactlytics();
  const cleanAnalyticsElementPrefix = `${analyticsElementPrefix?.toLowerCase().replace(/\s+/, '-')}`;
  const [activeTag, setActiveTag] = useState<number>(0);
  const filterTags = useMemo(() => getTags(articles), [oneDataArticlesData, articles]);
  const activeArticles = getActiveArticles(articles, activeTag);

  return (
    <>
      {heading && (
        <h2
          className="heading-sans-medium-regular pad-3-b pad-1-b-md-down pad-1-l-md-down"
          dangerouslySetInnerHTML={{ __html: sanitize(heading) }}
        />
      )}
      {filterTags.length > 1 && (
        <FilterButtons
          activeTag={activeTag}
          setActiveTag={(index) => setActiveTag(index)}
          tags={filterTags}
        />
      )}
      <div id="personalised-marketing-row" className="hidden-md-down row flex-justify-center">
        {activeArticles.map((article, index) => (
          <div
            data-testid={`${article?.id}-wrapper`}
            key={article?.id}
            className={clsx("width-full flex", {
              [styles.cardWrapperUnder4Articles]: activeArticles?.length < 4,
              [styles.cardWrapper]: activeArticles.length == 4
            })}
          >
            <ArticleOrAd
              data={article}
              index={index}
              analyticsElementPrefix={analyticsElementPrefix}
              adScreenSize={mediaQueries.LG_UP}
            />
          </div>
        ))}
      </div>
      <CardCarousel
        className="hidden-lg-up flex"
        id="personalised-marketing-carousel"
        shouldAutoPlay={false}
        slides={[...activeArticles.map((article, index) => (
          <ArticleOrAd
            data={article}
            index={index}
            analyticsElementPrefix={analyticsElementPrefix}
            adScreenSize={mediaQueries.LG_DOWN}
          />
        ))]}
        onSlideChange={(nextSlideId: string) => {
          if (analyticsElementPrefix) {
            track({
              event: {
                element: `${cleanAnalyticsElementPrefix}-${nextSlideId.toLowerCase().replace(/\s+/, '')}`,
                type: "slide-change",
                identifier: MODULE_NAME,
              },
            })
          }
        }}
      />
    </>
  );
};
